import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as seoShared from '../utils/seoShared';

/**
 * SEOManager - Gestionnaire centralisé du SEO pour Magic Park Times
 * 
 * Gère :
 * - Titres et descriptions optimisés par parc avec "temps d'attente + nom du parc"
 * - Sous-pages (shows, restaurants) avec SEO dédié
 * - URL canoniques
 * - Open Graph / Twitter Cards
 * - Données structurées JSON-LD (AmusementPark / WebApplication)
 * - Balise keywords
 * - robots noindex pour pages admin/auth
 * Les hreflang ne sont pas publiés tant que les routes localisées n'existent pas.
 */

const SITE_URL = seoShared.SITE_URL;
const ORGANIZATION_SCHEMA = seoShared.ORGANIZATION_SCHEMA;
const PARK_ENTITY_DETAILS = seoShared.PARK_ENTITY_DETAILS;



const STATIC_OG_IMAGE_BY_PATH = Object.keys(seoShared.STATIC_ROUTE_META).reduce((acc, routePath) => {
  acc[routePath] = seoShared.STATIC_ROUTE_META[routePath].image;
  return acc;
}, {});

// Pages à ne pas indexer
const NOINDEX_PATHS = [
  ...seoShared.NOINDEX_PATH_PREFIXES
];

// Mots-clés SEO de base par langue
const BASE_KEYWORDS = {
  fr: 'temps d\'attente, file d\'attente, parc d\'attractions, attractions, horaires, affluence',
  en: 'wait times, queue times, theme park, attractions, schedules, crowd levels',
  es: 'tiempos de espera, colas, parque de atracciones, atracciones, horarios, afluencia',
  de: 'Wartezeiten, Warteschlangen, Freizeitpark, Attraktionen, Öffnungszeiten, Besucheraufkommen',
  it: 'tempi di attesa, code, parco di divertimenti, attrazioni, orari, affluenza'
};

// Pays traduits par langue pour les descriptions
const COUNTRY_NAMES = {
  fr: {
    'France': 'France', 'Netherlands': 'Pays-Bas', 'Germany': 'Allemagne',
    'Belgium': 'Belgique', 'Italy': 'Italie', 'Spain': 'Espagne',
    'Sweden': 'Suède', 'Poland': 'Pologne', 'United States': 'États-Unis',
    'Japan': 'Japon', 'China': 'Chine', 'South Korea': 'Corée du Sud',
    'Saudi Arabia': 'Arabie saoudite'
  },
  en: {
    'France': 'France', 'Netherlands': 'Netherlands', 'Germany': 'Germany',
    'Belgium': 'Belgium', 'Italy': 'Italy', 'Spain': 'Spain',
    'Sweden': 'Sweden', 'Poland': 'Poland', 'United States': 'United States',
    'Japan': 'Japan', 'China': 'China', 'South Korea': 'South Korea',
    'Saudi Arabia': 'Saudi Arabia'
  },
  es: {
    'France': 'Francia', 'Netherlands': 'Países Bajos', 'Germany': 'Alemania',
    'Belgium': 'Bélgica', 'Italy': 'Italia', 'Spain': 'España',
    'Sweden': 'Suecia', 'Poland': 'Polonia', 'United States': 'Estados Unidos',
    'Japan': 'Japón', 'China': 'China', 'South Korea': 'Corea del Sur',
    'Saudi Arabia': 'Arabia Saudita'
  },
  de: {
    'France': 'Frankreich', 'Netherlands': 'Niederlande', 'Germany': 'Deutschland',
    'Belgium': 'Belgien', 'Italy': 'Italien', 'Spain': 'Spanien',
    'Sweden': 'Schweden', 'Poland': 'Polen', 'United States': 'Vereinigte Staaten',
    'Japan': 'Japan', 'China': 'China', 'South Korea': 'Südkorea',
    'Saudi Arabia': 'Saudi-Arabien'
  },
  it: {
    'France': 'Francia', 'Netherlands': 'Paesi Bassi', 'Germany': 'Germania',
    'Belgium': 'Belgio', 'Italy': 'Italia', 'Spain': 'Spagna',
    'Sweden': 'Svezia', 'Poland': 'Polonia', 'United States': 'Stati Uniti',
    'Japan': 'Giappone', 'China': 'Cina', 'South Korea': 'Corea del Sud',
    'Saudi Arabia': 'Arabia Saudita'
  }
};

// Coordonnées des parcs pour les données structurées
const PARK_GEO = seoShared.PARK_GEO;

// Codes pays ISO
const COUNTRY_CODES = {
  'France': 'FR', 'Netherlands': 'NL', 'Germany': 'DE',
  'Belgium': 'BE', 'Italy': 'IT', 'Spain': 'ES',
  'Sweden': 'SE', 'Poland': 'PL', 'United States': 'US',
  'Japan': 'JP', 'China': 'CN', 'South Korea': 'KR',
  'United Kingdom': 'GB', 'Saudi Arabia': 'SA'
};

/**
 * Détermine le type de sous-page
 * @param {string} path - Chemin URL
 * @returns {{ parkSlug: string|null, subPage: string|null }}
 */
const parseParkPath = (path) => {
  const { segments, internalParkSlug } = seoShared.getRouteInfo(path);
  if (segments.length === 0) return { parkSlug: null, subPage: null };

  const subPage = segments.length > 1 ? segments[1] : null;

  return { parkSlug: internalParkSlug, subPage };
};

const getStaticOgImageForPath = (path) => STATIC_OG_IMAGE_BY_PATH[seoShared.getCanonicalPath(path)] || null;



/**
 * Génère les données structurées JSON-LD pour un parc
 */
const generateParkJsonLd = (parkSlug, parkConfig, lang) => {
  const geo = PARK_GEO[parkSlug];
  const countryCode = COUNTRY_CODES[parkConfig.country] || parkConfig.country;
  const countryName = (COUNTRY_NAMES[lang] || COUNTRY_NAMES['en'])[parkConfig.country] || parkConfig.country;
  const entityDetails = PARK_ENTITY_DETAILS[parkSlug];
  const publicParkPath = seoShared.getPublicParkPath(parkSlug);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AmusementPark",
    "name": parkConfig.name,
    "description": `${parkConfig.name} - ${countryName}`,
    "url": `${SITE_URL}${publicParkPath}`,
    "image": `${SITE_URL}/images/og/og-${parkSlug}.jpg`,
    "address": entityDetails?.address || {
      "@type": "PostalAddress",
      "addressCountry": countryCode
    },
    "isAccessibleForFree": false,
    "publicAccess": true
  };

  if (geo) {
    jsonLd.geo = {
      "@type": "GeoCoordinates",
      "latitude": geo.lat,
      "longitude": geo.lng
    };
  }

  if (entityDetails?.sameAs) {
    jsonLd.sameAs = entityDetails.sameAs;
  }

  return jsonLd;
};

/**
 * Génère les données structurées WebPage pour relier chaque route SEO à Magic Park Times.
 */
const generateWebPageJsonLd = (cleanPath, title, description, ogImage, lang) => {
  const { segments, internalParkSlug } = seoShared.getRouteInfo(cleanPath);
  let mainParkSlug = internalParkSlug;

  if (segments[0] === 'portaventura' && segments[1] === 'ferrari-land') {
    mainParkSlug = 'ferrari-land';
  }

  const parkName = seoShared.PARK_NAMES[mainParkSlug];
  const canonicalPath = seoShared.getCanonicalPath(cleanPath);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${SITE_URL}${canonicalPath === '/' ? '' : canonicalPath}#webpage`,
    "url": `${SITE_URL}${canonicalPath === '/' ? '' : canonicalPath}`,
    "name": title.replace(/\s*\|\s*Magic Park Times$/, ''),
    "description": description,
    "inLanguage": lang,
    "image": ogImage,
    "isPartOf": {
      "@type": "WebSite",
      "name": "Magic Park Times",
      "url": SITE_URL
    },
    "publisher": {
      ...ORGANIZATION_SCHEMA
    }
  };

  if (parkName && segments.length <= 2) {
    const entityDetails = PARK_ENTITY_DETAILS[mainParkSlug];
    const geo = PARK_GEO[mainParkSlug];
    const publicParkPath = seoShared.getPublicParkPath(mainParkSlug);
    jsonLd.mainEntity = {
      "@type": "AmusementPark",
      "name": parkName,
      "url": `${SITE_URL}${publicParkPath}`,
      "image": `${SITE_URL}/images/og/og-${mainParkSlug}.jpg`
    };

    if (entityDetails?.address) {
      jsonLd.mainEntity.address = entityDetails.address;
    }
    if (entityDetails?.sameAs) {
      jsonLd.mainEntity.sameAs = entityDetails.sameAs;
    }
    if (geo) {
      jsonLd.mainEntity.geo = {
        "@type": "GeoCoordinates",
        "latitude": geo.lat,
        "longitude": geo.lng
      };
    }
  }

  return jsonLd;
};

/**
 * Parcs populaires pour le JSON-LD ItemList de la page d'accueil
 * Diversifié pour mettre en avant tous les parcs, pas seulement Disney
 */
const POPULAR_PARKS_FOR_ITEMLIST = seoShared.POPULAR_PARKS_FOR_ITEMLIST;

/**
 * Génère les données structurées JSON-LD pour la page d'accueil (WebApplication + ItemList)
 */
const generateHomeJsonLd = () => ([
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Magic Park Times",
    "url": SITE_URL,
    "applicationCategory": "TravelApplication",
    "operatingSystem": "Web",
    "description": "Live wait times for 60 theme parks worldwide including Europa-Park, Efteling, Parc Astérix, Phantasialand, Disneyland Paris, Universal Studios and more.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "author": {
      ...ORGANIZATION_SCHEMA
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Popular Theme Parks",
    "description": "Most popular theme parks with live wait times on Magic Park Times",
    "numberOfItems": POPULAR_PARKS_FOR_ITEMLIST.length,
    "itemListElement": POPULAR_PARKS_FOR_ITEMLIST.map((park, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": park.name,
      "url": `${SITE_URL}/${park.slug}`
    }))
  }
]);

const generateBreadcrumbJsonLd = (cleanPath, pageTitle) => {
  if (!cleanPath || cleanPath === '/') return null;

  const canonicalPath = seoShared.getCanonicalPath(cleanPath);
  const segments = canonicalPath.split('/').filter(Boolean);
  const itemListElement = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Magic Park Times",
      "item": SITE_URL
    }
  ];

  segments.forEach((segment, index) => {
    const pathPart = `/${segments.slice(0, index + 1).join('/')}`;
    const isLast = index === segments.length - 1;
    itemListElement.push({
      "@type": "ListItem",
      "position": index + 2,
      "name": isLast ? pageTitle.replace(/\s*\|\s*Magic Park Times$/, '') : (seoShared.PARK_NAMES[seoShared.getRouteInfo(pathPart).internalParkSlug] || segment.replace(/-/g, ' ')),
      "item": `${SITE_URL}${pathPart}`
    });
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": itemListElement
  };
};

const SEOManager = ({ parksConfig, homeDescription }) => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const path = location.pathname || '/';
  const lang = (i18n.language || 'fr').split('-')[0];

  const seoData = useMemo(() => {
    let title = '';
    let description = '';
    let keywords = BASE_KEYWORDS[lang] || BASE_KEYWORDS['en'];
    let jsonLd = null;
    let noindex = false;
    const cleanPath = seoShared.normalizePath(path);
    const canonicalPath = seoShared.getCanonicalPath(cleanPath);
    const sharedMeta = seoShared.getSeoMetaForRoute(cleanPath);
    const isKnownRoute = seoShared.isKnownSeoRoute(canonicalPath);
    // Image OG par défaut - remplacée par image statique/page/parc si disponible
    let ogImage = getStaticOgImageForPath(canonicalPath) || `${SITE_URL}/images/MPT_Logo2_NOIR.png`;

    // Vérifier si la page ne doit pas être indexée
    // Note: on vérifie /tv uniquement en fin de chemin ou suivi de / pour éviter de matcher /toverland
    if (!isKnownRoute || NOINDEX_PATHS.some(np => cleanPath.startsWith(np)) || cleanPath.endsWith('/tv') || cleanPath.includes('/tv/')) {
      noindex = true;
    }

    // === ROUTE INCONNUE / NOT FOUND ===
    if (!isKnownRoute) {
      title = sharedMeta.title;
      description = sharedMeta.description;
      ogImage = sharedMeta.ogImage;
    }
    // === PAGE D'ACCUEIL ===
    else if (cleanPath === '/') {
      title = t('meta.title.default');
      description = homeDescription || t('meta.description.default');
      keywords += ', Europa-Park, Efteling, Parc Astérix, Phantasialand, Disneyland Paris, PortAventura, Gardaland, Universal Studios, Futuroscope, Energylandia, Walibi';
      jsonLd = generateHomeJsonLd();
    }
    // === DISNEY ADVENTURE WORLD ===
    else if (canonicalPath === '/disney-adventure-world') {
      title = t('meta.title.studios');
      description = t('meta.description.studios');
      keywords += ', Disney Adventure World, Disneyland Paris';
      if (parksConfig && parksConfig['walt-disney-studios']) {
        jsonLd = generateParkJsonLd('walt-disney-studios', parksConfig['walt-disney-studios'], lang);
      }
    }
    // === SHOWS DLP ===
    else if (canonicalPath === '/shows') {
      title = t('meta.title.shows');
      description = t('meta.description.shows');
      keywords += ', spectacles, parades, Disneyland Paris';
    }
    // === RESTAURANTS DLP ===
    else if (canonicalPath === '/restaurants') {
      title = t('meta.title.restaurants');
      description = t('meta.description.restaurants');
      keywords += ', restaurants, Disneyland Paris';
    }
    // === LISTE DES PARCS ===
    else if (canonicalPath === '/parks') {
      title = t('meta.title.parks');
      description = t('meta.description.parks');
    }
    // === ABOUT ===
    else if (canonicalPath === '/about') {
      title = t('meta.title.about', 'À propos - Magic Park Times');
      description = t('meta.description.about', 'Découvrez Magic Park Times, la plateforme de référence pour les temps d\'attente dans les parcs d\'attractions.');
    }
    else if (canonicalPath === '/conditions-utilisation' || canonicalPath === '/conditions-generales-vente' || canonicalPath === '/terms') {
      title = t('legal.terms.pageTitle', 'Conditions d’utilisation et abonnements - Magic Park Times');
      description = t(
        'meta.description.terms',
        'Conditions d’utilisation et d’abonnement de Magic Park Times, incluant les comptes membres, les alertes temps d’attente, Stripe et la résiliation.'
      );
    }
    else if (canonicalPath === '/politique-confidentialite' || canonicalPath === '/privacy') {
      title = t('legal.privacy.pageTitle', 'Politique de confidentialité - Magic Park Times');
      description = t(
        'meta.description.privacy',
        'Politique de confidentialité de Magic Park Times, incluant les données de compte, de paiement, d’abonnement et de notifications.'
      );
    }
    // === CONTACT ===
    else if (canonicalPath === '/contact') {
      title = t('meta.title.contact', 'Contact - Magic Park Times');
      description = t('meta.description.contact', 'Contactez l\'équipe Magic Park Times.');
    }
    // === SUPPORT US ===
    else if (canonicalPath === '/support-us') {
      title = t('meta.title.supportUs', 'Nous soutenir - Magic Park Times');
      description = t('meta.description.supportUs', 'Soutenez Magic Park Times pour nous aider à maintenir et améliorer la plateforme.');
    }
    // === PAGES DE PARC ET SOUS-PAGES ===
    else if (canonicalPath !== '/') {
      const { parkSlug, subPage } = parseParkPath(canonicalPath);

      // Gestion spéciale Ferrari Land sous PortAventura
      let effectiveParkSlug = parkSlug;
      let effectiveSubPage = subPage;

      if (parkSlug === 'portaventura' && subPage === 'ferrari-land') {
        effectiveParkSlug = 'portaventura';
        // C'est la page Ferrari Land, pas une sous-page de PortAventura
        const ferrariConfig = parksConfig && parksConfig['ferrari-land'];
        if (ferrariConfig) {
          ogImage = `${SITE_URL}/images/og/og-ferrari-land.jpg`;
          title = t('meta.title.park', { parkName: ferrariConfig.name });
          description = t('meta.description.park', { parkName: ferrariConfig.name });
          keywords += `, ${ferrariConfig.name}, PortAventura World, ${(COUNTRY_NAMES[lang] || COUNTRY_NAMES['en'])[ferrariConfig.country] || ferrariConfig.country}`;
          jsonLd = generateParkJsonLd('ferrari-land', ferrariConfig, lang);
        }
        // Skip la logique ci-dessous
        effectiveSubPage = '__handled__';
      }

      if (effectiveSubPage !== '__handled__' && effectiveParkSlug && parksConfig && parksConfig[effectiveParkSlug]) {
        const parkConfig = parksConfig[effectiveParkSlug];
        const parkName = parkConfig.name;
        const countryName = (COUNTRY_NAMES[lang] || COUNTRY_NAMES['en'])[parkConfig.country] || parkConfig.country;
        // Pour toute route d'un parc (page principale + sous-pages), garder une image OG liée au parc
        ogImage = `${SITE_URL}/images/og/og-${effectiveParkSlug}.jpg`;

        if (!effectiveSubPage) {
          // === PAGE PRINCIPALE DU PARC ===
          if (effectiveParkSlug === 'puydufou') {
            ogImage = sharedMeta.ogImage;
            title = t('meta.title.puyDuFou', {
              defaultValue: sharedMeta.title
            });
            description = t('meta.description.puyDuFou', {
              defaultValue: sharedMeta.description
            });
            keywords += ', Puy du Fou programme, programme du jour Puy du Fou, horaires spectacles Puy du Fou, programme spectacle Puy du Fou, horaires ouverture Puy du Fou';
          } else {
            title = t('meta.title.park', { parkName });
            description = t('meta.description.park', { parkName });
            keywords += `, ${parkName}, ${countryName}`;
          }
          jsonLd = generateParkJsonLd(effectiveParkSlug, parkConfig, lang);
        } else if (effectiveSubPage === 'shows') {
          // === PAGE DES SPECTACLES D'UN PARC ===
          title = t('meta.title.parkShows', {
            parkName,
            defaultValue: `Spectacles ${parkName} - Horaires en direct | Magic Park Times`
          });
          description = t('meta.description.parkShows', {
            parkName,
            defaultValue: `Horaires des spectacles et événements à ${parkName}. Consultez les prochains shows et planifiez votre journée avec Magic Park Times.`
          });
          keywords += `, ${parkName}, spectacles, shows, horaires, ${countryName}`;
        } else if (effectiveSubPage === 'restaurants') {
          // === PAGE DES RESTAURANTS D'UN PARC ===
          title = t('meta.title.parkRestaurants', {
            parkName,
            defaultValue: `Restaurants ${parkName} - Guide & Infos | Magic Park Times`
          });
          description = t('meta.description.parkRestaurants', {
            parkName,
            defaultValue: `Découvrez tous les restaurants et points de restauration de ${parkName}. Menus, emplacements et informations pratiques sur Magic Park Times.`
          });
          keywords += `, ${parkName}, restaurants, restauration, ${countryName}`;
        } else if (effectiveSubPage === 'map') {
          // === PAGE CARTE INTERACTIVE ===
          title = t('meta.title.parkMap', {
            parkName,
            defaultValue: `Carte interactive ${parkName} | Magic Park Times`
          });
          description = t('meta.description.parkMap', {
            parkName,
            defaultValue: `Carte interactive de ${parkName} avec localisation des attractions et temps d'attente en temps réel.`
          });
          keywords += `, ${parkName}, carte, map, plan, ${countryName}`;
        }
      }
      // Disneyland Resort sub-pages
      else if (effectiveParkSlug === 'disneyland-resort') {
        if (effectiveSubPage === 'shows') {
          title = t('meta.title.disneylandResortShows', {
            defaultValue: 'Spectacles Disneyland Resort - Horaires | Magic Park Times'
          });
          description = t('meta.description.disneylandResortShows', {
            defaultValue: 'Horaires des spectacles et parades au Disneyland Resort California. Planifiez votre visite avec Magic Park Times.'
          });
          keywords += ', Disneyland Resort, California, spectacles, shows';
        } else if (effectiveSubPage === 'restaurants') {
          title = t('meta.title.disneylandResortRestaurants', {
            defaultValue: 'Restaurants Disneyland Resort - Guide | Magic Park Times'
          });
          description = t('meta.description.disneylandResortRestaurants', {
            defaultValue: 'Découvrez tous les restaurants du Disneyland Resort California. Guide complet avec Magic Park Times.'
          });
          keywords += ', Disneyland Resort, California, restaurants';
        }
      }
    }

    // Fallback si rien n'a matché
    if (!title) {
      title = t('meta.title.default');
      description = t('meta.description.default');
    }

    // Nettoyage du chemin canonique
    const canonicalUrl = sharedMeta.canonicalUrl;
    const webPageJsonLd = !noindex && canonicalPath !== '/'
      ? generateWebPageJsonLd(canonicalPath, title, description, ogImage, lang)
      : null;
    if (webPageJsonLd) {
      jsonLd = jsonLd
        ? [...(Array.isArray(jsonLd) ? jsonLd : [jsonLd]), webPageJsonLd]
        : webPageJsonLd;
    }

    const breadcrumbJsonLd = generateBreadcrumbJsonLd(canonicalPath, title);
    if (breadcrumbJsonLd) {
      jsonLd = jsonLd
        ? [...(Array.isArray(jsonLd) ? jsonLd : [jsonLd]), breadcrumbJsonLd]
        : breadcrumbJsonLd;
    }

    return { title, description, keywords, jsonLd, noindex, ogImage, canonicalUrl };
  }, [path, parksConfig, homeDescription, t, lang]);

  return (
    <Helmet>
      {/* Balises standards */}
      <title>{seoData.title}</title>
      <meta name="description" content={seoData.description} />
      <meta name="keywords" content={seoData.keywords} />
      <link rel="canonical" href={seoData.canonicalUrl} />



      {/* Robots */}
      {seoData.noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={seoData.canonicalUrl} />
      <meta property="og:title" content={seoData.title} />
      <meta property="og:description" content={seoData.description} />
      <meta property="og:image" content={seoData.ogImage} />
      <meta property="og:image:secure_url" content={seoData.ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={seoData.title} />
      <meta property="og:site_name" content="Magic Park Times" />
      <meta property="og:locale" content={lang === 'fr' ? 'fr_FR' : lang === 'en' ? 'en_US' : lang === 'es' ? 'es_ES' : lang === 'de' ? 'de_DE' : 'it_IT'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoData.canonicalUrl} />
      <meta name="twitter:title" content={seoData.title} />
      <meta name="twitter:description" content={seoData.description} />
      <meta name="twitter:image" content={seoData.ogImage} />
      <meta name="twitter:image:alt" content={seoData.title} />

      {/* Données structurées JSON-LD */}
      {seoData.jsonLd && (
        Array.isArray(seoData.jsonLd) ? (
          seoData.jsonLd.map((ld, i) => (
            <script key={`ld-${i}`} type="application/ld+json">
              {JSON.stringify(ld)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(seoData.jsonLd)}
          </script>
        )
      )}
    </Helmet>
  );
};

export default SEOManager;
